import React, { useEffect, useMemo, useState } from 'react';

/**
 * Accueil du Sourceur v2 — « Vos campagnes ».
 *
 * Le pupitre d'opérations du sourceur : quatre compteurs, la liste des
 * campagnes filtrée par état réel, et un rail « À traiter / Activité
 * récente ». TOUT vient des lignes de GET /sourcing (fusionnées par le
 * parent) — aucune tâche inventée : « À traiter » = sélections prêtes à
 * valider et recherches en erreur, « Activité récente » = les derniers
 * mouvements horodatés des mêmes campagnes.
 */

const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} />;
const mono = { fontFamily: "'Space Mono','SFMono-Regular',Consolas,monospace" };

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('fr-CH', { day: '2-digit', month: 'short' }) + ' ' + d.toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' });
};
const pad2 = (n) => String(n).padStart(2, '0');

// « 3/12 » → { envoyes: 3, total: 12 } — le compteur InMails du parent.
const lireCompteur = (c) => {
  const m = /^(\d+)\s*\/\s*(\d+)$/.exec(String(c || '').trim());
  return m ? { envoyes: Number(m[1]), total: Number(m[2]) } : null;
};
const lireSelection = (s) => { const m = /^(\d+)/.exec(String(s || '').trim()); return m ? Number(m[1]) : 0; };

// L'état d'une ligne, dérivé des champs réels posés par mergeCampaigns.
const etatDe = (r) => {
  if (r.deleted) return 'morte';
  if (r.pulse) return 'recherche';
  if (r.chip?.k === 'bad') return 'erreur';
  if (r.chip?.k === 'ok') return 'prete';
  if (r.counter) return 'inmails';
  return 'autre';
};
const DOT = { recherche: 'sva-dot--recherche', prete: 'sva-dot--prete', erreur: 'sva-dot--erreur' };

const CHIP_STYLES = {
  ok: { background: 'rgba(22,163,74,0.13)', color: 'var(--ok)' },
  warn: { background: 'var(--erp-accent-soft)', color: 'var(--erp-accent)' },
  bad: { background: 'var(--danger-bg)', color: 'var(--danger-fg)' },
  mut: { background: 'var(--erp-surface-muted)', color: 'var(--erp-text-secondary)' },
};

const PAGE_LEN = 10;

export default function SourceurV2Accueil({ rows, chargement, routeAbsente, rafraichit, onSelectBesoin, onNouvelleCampagne, onSuiviDirect, onRefresh }) {
  const lignes = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);
  const [etat, setEtat] = useState('tous');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);

  const vivantes = useMemo(() => lignes.filter(r => !r.deleted), [lignes]);
  const enRecherche = vivantes.filter(r => etatDe(r) === 'recherche');
  const pretes = vivantes.filter(r => etatDe(r) === 'prete');
  const erreurs = vivantes.filter(r => etatDe(r) === 'erreur');
  const compteurs = vivantes.map(r => lireCompteur(r.counter)).filter(Boolean);
  const inmailsEnvoyes = compteurs.reduce((s, c) => s + c.envoyes, 0);
  const inmailsTotal = compteurs.reduce((s, c) => s + c.total, 0);
  const selectionnes = pretes.reduce((s, r) => s + lireSelection(r.selLabel), 0);
  const derniere = lignes[0] || null; // le parent trie par activité décroissante

  const pills = useMemo(() => {
    const defs = [
      ['tous', 'Toutes', vivantes.length],
      ['recherche', 'En recherche', enRecherche.length],
      ['prete', 'Sélection prête', pretes.length],
      ['inmails', 'InMails', vivantes.filter(r => etatDe(r) === 'inmails').length],
      ['erreur', 'En erreur', erreurs.length],
    ];
    return defs.filter(([k, , n]) => k === 'tous' || n > 0);
  }, [vivantes, enRecherche.length, pretes.length, erreurs.length]);

  const filtrees = useMemo(() => {
    const t = q.trim().toLowerCase();
    return lignes.filter(r => {
      if (etat !== 'tous' && etatDe(r) !== etat) return false;
      if (t && !`${r.titre || ''} ${r.query || ''}`.toLowerCase().includes(t)) return false;
      return true;
    });
  }, [lignes, etat, q]);
  const pages = Math.max(1, Math.ceil(filtrees.length / PAGE_LEN));
  useEffect(() => { if (page > pages) setPage(pages); }, [pages, page]);
  const pageLignes = filtrees.slice((page - 1) * PAGE_LEN, page * PAGE_LEN);
  const choisir = (k) => { setEtat(k); setPage(1); };

  // À traiter : les seules actions réellement en attente côté humain.
  const aTraiter = useMemo(() => ([
    ...pretes.map(r => ({ icone: 'check2-circle', titre: 'Sélection prête à valider', r })),
    ...erreurs.map(r => ({ icone: 'exclamation-triangle', titre: 'Recherche en erreur — à relancer', r })),
  ].slice(0, 5)), [pretes, erreurs]);
  const recentes = lignes.filter(r => r.when).slice(0, 4);

  const kpiDate = derniere?.when ? fmtDate(derniere.when) : '—';

  return (
    <div style={{ fontFamily: "'DM Sans','IBM Plex Sans',sans-serif" }}>
      {/* ── En-tête ── */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '12px', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: '250px' }}>
          <div className="sva-kicker">
            Espace sourceur · Vue opérationnelle · {new Date().toLocaleDateString('fr-CH', { weekday: 'long', day: '2-digit', month: 'long' })}
          </div>
          <h1 className="sva-titre">Vos campagnes<span className="sva-point">.</span></h1>
          <div className="sva-sous">Gardez une vue nette sur vos recherches, vos sélections et les prochaines actions.</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '9px', flex: 'none', flexWrap: 'wrap' }}>
          {onSuiviDirect && (
            <button type="button" className="erp-btn erp-btn--secondary" onClick={onSuiviDirect}>
              <BI icon="broadcast" style={{ fontSize: '12px' }} /> Suivi en direct
            </button>
          )}
          {onRefresh && !routeAbsente && (
            <button type="button" className="erp-btn erp-btn--secondary" onClick={onRefresh} disabled={rafraichit} aria-label="Rafraîchir la liste">
              <BI icon="arrow-repeat" style={rafraichit ? { animation: 'ats-spin .7s linear infinite' } : undefined} />
            </button>
          )}
          <button type="button" className="erp-btn erp-btn--primary" onClick={onNouvelleCampagne}>
            <BI icon="plus-lg" style={{ fontSize: '12px' }} /> Nouvelle campagne
          </button>
        </div>
      </div>

      {routeAbsente && (
        <div className="erp-card" style={{ marginTop: '16px', padding: '13px 16px', fontSize: '12.5px', color: 'var(--erp-text-secondary)' }}>
          La liste des campagnes arrive côté serveur — vous pouvez déjà lancer une campagne via « Nouvelle campagne ».
        </div>
      )}

      {!routeAbsente && (
        <>
          {/* ── Compteurs ── */}
          <div className="sva-kpis">
            <div className="sva-kpi">
              <div className="sva-kpi-lbl">Campagnes actives</div>
              <BI icon="briefcase" style={{ position: 'absolute', top: '11px', right: '13px', fontSize: '13px', color: 'var(--erp-text-tertiary)' }} />
              <div className="sva-kpi-num">{pad2(vivantes.length)}</div>
              <div className="sva-kpi-sub">{enRecherche.length > 0 ? `${enRecherche.length} recherche${enRecherche.length > 1 ? 's' : ''} en cours` : 'aucune recherche en cours'}</div>
            </div>
            <div className="sva-kpi">
              <div className="sva-kpi-lbl">Sélections prêtes</div>
              <BI icon="check2-circle" style={{ position: 'absolute', top: '11px', right: '13px', fontSize: '13px', color: 'var(--erp-text-tertiary)' }} />
              <div className="sva-kpi-num">{pad2(pretes.length)}</div>
              <div className="sva-kpi-sub">{selectionnes > 0 ? `${selectionnes} profil${selectionnes > 1 ? 's' : ''} à valider` : 'rien à valider pour l’instant'}</div>
            </div>
            <div className="sva-kpi">
              <div className="sva-kpi-lbl">InMails envoyés</div>
              <BI icon="send" style={{ position: 'absolute', top: '11px', right: '13px', fontSize: '13px', color: 'var(--erp-text-tertiary)' }} />
              <div className="sva-kpi-num">{pad2(inmailsEnvoyes)}</div>
              <div className="sva-kpi-sub">{inmailsTotal > 0 ? `sur ${inmailsTotal} prévus` : 'aucun envoi en cours'}</div>
            </div>
            <div className="sva-kpi">
              <div className="sva-kpi-lbl">Dernière activité</div>
              <BI icon="clock-history" style={{ position: 'absolute', top: '11px', right: '13px', fontSize: '13px', color: 'var(--erp-text-tertiary)' }} />
              <div className="sva-kpi-num" style={{ fontSize: '17px', marginTop: '11px' }}>{kpiDate}</div>
              <div className="sva-kpi-sub">{derniere?.titre || 'en attente de la première campagne'}</div>
            </div>
          </div>

          {/* ── Liste + rail ── */}
          <div className="sva-cols">
            <div className="erp-card" style={{ overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 16px', borderBottom: '1px solid var(--hair)', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '13.5px', fontWeight: 700, color: 'var(--erp-ink-strong)' }}>Toutes vos campagnes</span>
                <span style={{ ...mono, fontSize: '10px', color: 'var(--erp-text-tertiary)' }}>{filtrees.length} résultat{filtrees.length > 1 ? 's' : ''}</span>
                <div style={{ flex: 1 }} />
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '2px', padding: '3px', borderRadius: '99px', background: 'var(--erp-surface-muted)', border: '1px solid var(--erp-line)' }}>
                  {pills.map(([k, label, n]) => (
                    <button key={k} type="button" onClick={() => choisir(k)} className={`sva-pill${etat === k ? ' sva-pill--on' : ''}`}>
                      {label}{k !== 'tous' && <b>{n}</b>}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--hair)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '9px', padding: '0 12px', borderRadius: '9px', border: '1px solid var(--erp-field-border)', background: 'var(--erp-surface)' }}>
                  <BI icon="search" style={{ color: 'var(--erp-text-tertiary)', fontSize: '13px' }} />
                  <input value={q} onChange={e => { setQ(e.target.value); setPage(1); }} placeholder="Rechercher une campagne, une requête…"
                    style={{ flex: 1, minWidth: 0, padding: '9px 0', border: 'none', background: 'transparent', color: 'var(--erp-ink-strong)', fontFamily: 'inherit', fontSize: '13px', outline: 'none' }} />
                </div>
              </div>

              {chargement && (
                <div style={{ padding: '18px 16px', fontSize: '12.5px', color: 'var(--erp-text-tertiary)' }}>
                  <BI icon="arrow-repeat" style={{ display: 'inline-block', animation: 'ats-spin .8s linear infinite', marginRight: '7px' }} />Chargement des campagnes…
                </div>
              )}
              {!chargement && lignes.length === 0 && (
                <div style={{ padding: '44px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '11px', textAlign: 'center' }}>
                  <div style={{ width: '48px', height: '48px', borderRadius: '13px', background: 'var(--erp-surface-muted)', color: 'var(--erp-text-tertiary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><BI icon="linkedin" style={{ fontSize: '20px' }} /></div>
                  <div style={{ fontSize: '16px', fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--erp-ink-strong)' }}>Aucune campagne pour l&rsquo;instant</div>
                  <div style={{ maxWidth: '400px', fontSize: '12.5px', lineHeight: 1.55, color: 'var(--erp-text-secondary)' }}>Choisissez un besoin, décrivez la cible et Erpia chasse les meilleurs profils LinkedIn — la campagne vivra ici.</div>
                  <button type="button" className="erp-btn erp-btn--primary" onClick={onNouvelleCampagne} style={{ marginTop: '3px' }}>
                    <BI icon="plus-lg" style={{ fontSize: '12px' }} /> Lancer la première campagne
                  </button>
                </div>
              )}
              {!chargement && lignes.length > 0 && filtrees.length === 0 && (
                <div style={{ padding: '18px 16px', fontSize: '12.5px', color: 'var(--erp-text-tertiary)' }}>Aucune campagne ne correspond — élargissez le filtre ou videz la recherche.</div>
              )}

              {pageLignes.map((r, i) => {
                const e = etatDe(r);
                const cpt = lireCompteur(r.counter);
                const chipStyle = CHIP_STYLES[r.chip?.k] || CHIP_STYLES.mut;
                return (
                  <div key={r.besoin_id ?? `l${i}`} className={`sva-row${r.deleted ? ' sva-row--morte' : ''}`} title={r.error || undefined}
                    onClick={r.deleted ? undefined : () => onSelectBesoin?.(r.besoin_id)}
                    role={r.deleted ? undefined : 'button'} tabIndex={r.deleted ? undefined : 0}
                    onKeyDown={r.deleted ? undefined : (ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); onSelectBesoin?.(r.besoin_id); } }}>
                    <span className={`sva-dot ${DOT[e] || ''}`} />
                    <div style={{ flex: 1.4, minWidth: 0 }}>
                      <div style={{ fontSize: '13.5px', fontWeight: 700, letterSpacing: '-0.01em', color: r.deleted ? 'var(--erp-muted, var(--text-faint))' : 'var(--erp-ink-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {r.deleted ? 'Besoin supprimé' : r.titre}
                      </div>
                      {(r.query || r.experience || (r.exclusions || []).length > 0) && (
                        <div style={{ ...mono, marginTop: '2px', fontSize: '10.5px', color: 'var(--erp-text-tertiary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {[r.query, r.experience ? `exp. ${r.experience}` : '', (r.exclusions || []).length ? `sans ${r.exclusions.join(', ')}` : ''].filter(Boolean).join(' · ')}
                        </div>
                      )}
                    </div>
                    {r.selLabel && <span style={{ fontSize: '11.5px', color: 'var(--erp-text-secondary)', flex: 'none' }}>{r.selLabel}</span>}
                    {cpt && cpt.total > 0 && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', flex: 'none' }} title="InMails envoyés / total">
                        <span className="sva-prog"><span style={{ width: `${Math.min(100, Math.round((cpt.envoyes / cpt.total) * 100))}%` }} /></span>
                        <span style={{ ...mono, fontSize: '10.5px', color: 'var(--erp-text-secondary)' }}>{cpt.envoyes}/{cpt.total}</span>
                      </span>
                    )}
                    {r.chip && (
                      <span style={{ ...chipStyle, fontSize: '10.5px', fontWeight: 700, padding: '3px 9px', borderRadius: '6px', whiteSpace: 'nowrap', flex: 'none' }}>{r.chip.l}</span>
                    )}
                    <span className="sva-heure" style={{ width: '76px', textAlign: 'right' }}>{fmtDate(r.when)}</span>
                    {!r.deleted && <BI icon="chevron-right" style={{ color: 'var(--erp-text-tertiary)', fontSize: '11px', flex: 'none' }} />}
                  </div>
                );
              })}

              {pages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '12px' }}>
                  <div className="ats-pager">
                    <button className="ats-pager-nav" onClick={() => setPage(page - 1)} disabled={page <= 1} aria-label="Page précédente"><BI icon="chevron-left" /></button>
                    {Array.from({ length: pages }, (_, i) => i + 1).filter(p => Math.abs(p - page) <= 2 || p === 1 || p === pages).map((p, i, arr) => (
                      <React.Fragment key={p}>
                        {i > 0 && arr[i - 1] !== p - 1 && <span className="ats-pager-gap">…</span>}
                        <button onClick={() => setPage(p)} aria-current={p === page ? 'page' : undefined}>{p}</button>
                      </React.Fragment>
                    ))}
                    <button className="ats-pager-nav" onClick={() => setPage(page + 1)} disabled={page >= pages} aria-label="Page suivante"><BI icon="chevron-right" /></button>
                  </div>
                </div>
              )}
            </div>

            {/* ── Rail ── */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', minWidth: 0 }}>
              <div className="erp-card" style={{ padding: '13px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span className="sva-rail-titre">À traiter</span>
                  <span style={{ flex: 1 }} />
                  <BI icon="list-task" style={{ color: 'var(--erp-accent)', fontSize: '12px' }} />
                </div>
                {aTraiter.length === 0 && (
                  <div style={{ marginTop: '9px', fontSize: '12px', lineHeight: 1.5, color: 'var(--erp-text-secondary)' }}>Rien à traiter — les campagnes avancent toutes seules.</div>
                )}
                {aTraiter.map(({ icone, titre, r }, i) => (
                  <button key={`${r.besoin_id}-${i}`} type="button" className="sva-rail-item" onClick={() => onSelectBesoin?.(r.besoin_id)} title={r.error || undefined}>
                    <BI icon={icone} style={{ flex: 'none', marginTop: '2px', fontSize: '12px', color: icone === 'exclamation-triangle' ? 'var(--danger-fg)' : 'var(--erp-accent)' }} />
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span className="sva-rail-item-titre" style={{ display: 'block' }}>{titre}</span>
                      <span className="sva-rail-item-sub" style={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.titre}</span>
                    </span>
                    <BI icon="chevron-right" style={{ flex: 'none', marginTop: '3px', fontSize: '10px', color: 'var(--erp-text-tertiary)' }} />
                  </button>
                ))}
              </div>

              <div className="erp-card" style={{ padding: '13px 16px' }}>
                <div className="sva-rail-titre" style={{ marginBottom: '3px' }}>Activité récente</div>
                {recentes.length === 0 && (
                  <div style={{ marginTop: '7px', fontSize: '12px', color: 'var(--erp-text-secondary)' }}>Le journal se remplira avec la première campagne.</div>
                )}
                {recentes.map((r, i) => (
                  <button key={r.besoin_id ?? `a${i}`} type="button" className="sva-rail-item" disabled={r.deleted}
                    onClick={r.deleted ? undefined : () => onSelectBesoin?.(r.besoin_id)} style={r.deleted ? { cursor: 'default' } : undefined}>
                    <span className="sva-heure" style={{ marginTop: '2px', width: '74px' }}>{fmtDate(r.when)}</span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span className="sva-rail-item-titre" style={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.deleted ? 'Besoin supprimé' : r.titre}</span>
                      <span className="sva-rail-item-sub" style={{ display: 'block' }}>{r.chip?.l}</span>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
